import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Loader2, FileText, Plane, Hotel, Bus, Train, Car, Briefcase, Search,
  Download, Printer, Mail, Trash2, RefreshCw, X, BadgeCheck, AlertCircle, RotateCcw,
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_META = {
  flight:  { label: 'Flight',         Icon: Plane },
  hotel:   { label: 'Hotel',          Icon: Hotel },
  bus:     { label: 'Bus',            Icon: Bus },
  train:   { label: 'Train',          Icon: Train },
  cab:     { label: 'Cab',            Icon: Car },
  holiday: { label: 'Holiday Package',Icon: Briefcase },
};

const STATUS_STYLE = {
  paid:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  pending:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  refunded: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─────────────────────────────────────────────────────────────────────────────
// Top KPI tiles
// ─────────────────────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, accent = 'gray', testId }) => (
  <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid={testId}>
    <CardContent className="p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold text-${accent}-700 dark:text-${accent}-300`}>{value}</p>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// Invoice preview modal — MMT-style tax invoice with Elite Travo branding
// ─────────────────────────────────────────────────────────────────────────────
// Production business defaults (will be overridden by /api/public/business once loaded,
// or by invoice.business snapshot embedded in the invoice document).
const DEFAULT_BUSINESS = {
  trade_name: 'EliteTravo',
  legal_name: 'EliteTravo',
  gstin: '27EZBPB2831J1ZK',
  pan: 'EZBPB2831J',
  constitution: 'Proprietorship',
  state: 'Maharashtra',
  state_code: '27',
  address_oneline: 'Room No. 1006, Sai Nagar Chawl Committee, Bhut Bunglow, Devipada Borivali, Mumbai Suburban, Maharashtra – 400066',
  support_email: 'support@elitetravo.com',
  support_phone: '+91 90000 00000',
  website_url: 'https://elitetravo.com',
  invoice_verify_url_prefix: 'https://elitetravo.com/invoice/verify',
};

const WATERMARK_COPY = {
  paid:      { text: 'PAID',      color: '#16a34a' },
  pending:   { text: 'PENDING',   color: '#d97706' },
  refunded:  { text: 'REFUNDED',  color: '#2563eb' },
  cancelled: { text: 'CANCELLED', color: '#dc2626' },
  failed:    { text: 'FAILED',    color: '#dc2626' },
};

// ─────────────────────────────────────────────────────────────────────────────
const InvoicePreview = ({ invoice, onClose, onEmail, onDelete, businessInfo }) => {
  const printableRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  if (!invoice) return null;
  const {
    customer = {}, billing_address: addr = {}, supplier = {},
    payment_breakup: pb = {}, travel_details: td = {},
  } = invoice;

  // Embedded snapshot wins; otherwise the live /public/business prop; otherwise defaults.
  const biz = invoice.business || businessInfo || DEFAULT_BUSINESS;
  const verifyUrl = `${biz.invoice_verify_url_prefix || DEFAULT_BUSINESS.invoice_verify_url_prefix}/${invoice.invoice_number}`;
  const qrPayload = JSON.stringify({
    inv: invoice.invoice_number,
    bk: invoice.booking_id,
    cust: customer.name || '',
    amt: pb.grand_total ?? invoice.total_amount ?? 0,
    gstin: biz.gstin,
    url: verifyUrl,
  });
  const watermark = WATERMARK_COPY[(invoice.payment_status || '').toLowerCase()] || null;

  // Generate PDF that is a 1:1 visual copy of the on-screen invoice preview.
  // Uses html2pdf (html2canvas + jsPDF) to capture the actual rendered DOM —
  // every CSS rule, image, padding and font size is preserved.
  const getPrintableNode = () =>
    printableRef.current || document.getElementById('invoice-printable');

  const generatePdf = () => {
    const node = getPrintableNode();
    if (!node) return null;
    // Add an export-class so .pdf-export rules can fix any mobile-responsive
    // grid issues during capture without affecting the live preview.
    node.classList.add('pdf-export');
    const opt = {
      margin:       [0.4, 0.4, 0.4, 0.4],          // inches — symmetric A4 margins
      filename:     `${invoice.invoice_number || 'invoice'}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  {
        scale: 2,                                    // high DPI, crisp text
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: node.scrollWidth,
        letterRendering: true,
      },
      jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait', compress: true },
      pagebreak:    { mode: ['css', 'legacy'], avoid: ['tr', '.kv', '.pay-row', '.no-break'] },
    };
    try {
      const worker = html2pdf().set(opt).from(node);
      return worker;
    } finally {
      // Class removed after worker resolves (handlers below)
      // We'll remove it in finally blocks at call sites.
    }
  };

  const cleanupExportClass = () => {
    const node = getPrintableNode();
    if (node) node.classList.remove('pdf-export');
  };

  const handleDownload = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const worker = generatePdf();
      if (worker) await worker.save();
    } catch (e) {
      console.error('[invoice-pdf] PDF generation failed:', e);
    } finally {
      cleanupExportClass();
      setGenerating(false);
    }
  };

  const handlePrint = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const worker = generatePdf();
      if (!worker) return;
      const blob = await worker.outputPdf('blob');
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'width=900,height=1100');
      if (win) {
        // Some browsers need a moment after load to trigger print
        win.addEventListener('load', () => { try { win.print(); } catch { /* noop */ } }, { once: true });
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 60000);
      } else {
        // Popup blocked — fall back to downloading the PDF
        await worker.save();
      }
    } catch (e) {
      console.error('Print failed:', e);
    } finally {
      cleanupExportClass();
      setGenerating(false);
    }
  };

  // MMT-style "Sun, 15 Jun 2025" formatter
  const fmtMmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Map booking_type → primary entity label + city/date keys we'll show prominently
  const primaryBlock = (() => {
    if (invoice.booking_type === 'hotel') {
      return [
        ['Hotel Name', td.hotel_name],
        ['Hotel City', String(td.city || '').toUpperCase()],
        ['Check-in',   fmtMmt(td.check_in)],
        ['Check-Out',  fmtMmt(td.check_out)],
      ];
    }
    if (invoice.booking_type === 'flight') {
      return [
        ['Airline',     td.airline],
        ['Flight No.',  td.flight_number],
        ['Sector',      `${td.origin} → ${td.destination}`],
        ['Departure',   fmtMmt(td.departure)],
      ];
    }
    if (invoice.booking_type === 'bus') {
      return [
        ['Operator',     td.operator],
        ['Route',        `${td.origin} → ${td.destination}`],
        ['Travel Date',  fmtMmt(td.travel_date)],
        ['Seat Type',    td.seat_type],
      ];
    }
    if (invoice.booking_type === 'train') {
      return [
        ['Train',        `${td.train_number} ${td.train_name || ''}`.trim()],
        ['Route',        `${td.origin} → ${td.destination}`],
        ['Travel Date',  fmtMmt(td.travel_date)],
        ['Class',        td.class],
      ];
    }
    if (invoice.booking_type === 'cab') {
      return [
        ['Vehicle',     td.vehicle],
        ['Route',       `${td.pickup} → ${td.drop}`],
        ['Trip Type',   td.trip_type],
        ['Pickup',      fmtMmt(td.pickup_time)],
      ];
    }
    return [
      ['Package',      td.package_name],
      ['Destination',  td.destination],
      ['Start',        fmtMmt(td.start_date)],
      ['End',          fmtMmt(td.end_date)],
    ];
  })();

  const KV = ({ label, value, bold }) => (
    <div className="flex items-start gap-3 py-1">
      <span className="text-[11px] uppercase tracking-wide text-gray-500 min-w-[140px] flex-shrink-0">{label}</span>
      <span className={`text-[12px] text-gray-900 dark:text-gray-100 ${bold ? 'font-semibold' : ''}`}>{value || '—'}</span>
    </div>
  );

  const PayRow = ({ label, sub, value, bold = false, sign = false }) => (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className={`text-[12px] ${bold ? 'font-semibold' : ''} text-gray-900 dark:text-gray-100`}>{label}</p>
        {sub && <p className="text-[10px] text-gray-500 italic mt-0.5">{sub}</p>}
      </div>
      <p className={`text-[12px] ${bold ? 'font-bold' : ''} text-gray-900 dark:text-gray-100 whitespace-nowrap`}>
        {sign && value > 0 ? '+' : ''}{value < 0 ? '-' : ''}₹{Math.abs(Number(value || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="invoice-preview-modal"
    >
      <div
        className="bg-white dark:bg-gray-900 w-full max-w-3xl max-h-[94vh] rounded-xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 print:hidden">
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{invoice.invoice_number}</p>
            <p className="text-[11px] text-gray-500">{TYPE_META[invoice.booking_type]?.label || invoice.booking_type} · {fmtDate(invoice.invoice_date)}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <Button size="sm" variant="outline" onClick={handleDownload} disabled={generating} data-testid="invoice-download-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
              PDF
            </Button>
            <Button size="sm" variant="outline" onClick={handlePrint} disabled={generating} data-testid="invoice-print-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Printer className="h-3.5 w-3.5 mr-1.5" />}
              Print
            </Button>
            <Button size="sm" variant="outline" onClick={() => onEmail(invoice)} data-testid="invoice-email-btn"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</Button>
            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30" onClick={() => onDelete(invoice)} data-testid="invoice-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close" data-testid="invoice-close-btn"><X className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Printable body — MMT-style. Captured 1:1 to PDF via html2pdf. */}
        <div className="overflow-y-auto bg-white text-gray-900" id="invoice-printable" ref={printableRef}>
          <div className="invoice-page p-6 sm:p-8 relative" style={{ width: '7.27in' }}>
            {/* Status watermark (large, diagonal, semi-transparent) */}
            {watermark && (
              <div className="invoice-watermark" aria-hidden="true" style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none', zIndex: 1, opacity: 0.10,
              }}>
                <span style={{
                  transform: 'rotate(-22deg)', fontSize: '120px', fontWeight: 900,
                  letterSpacing: '8px', color: watermark.color, whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                }}>{watermark.text}</span>
              </div>
            )}
            <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Top banner: TAX INVOICE pill (left) · Elite Travo logo (center) · Company address (right) */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="bg-red-600 text-white text-[11px] font-extrabold tracking-widest uppercase px-3 py-1.5 rounded">Tax Invoice</div>
              <div className="flex items-center gap-2">
                <img src="/elite-travo-logo.png" alt="Elite Travo" className="h-9 w-9 object-contain" />
                <p className="text-lg font-extrabold leading-tight"><span className="text-gray-900">Elite</span><span className="text-red-600">Travo</span></p>
              </div>
              <div className="text-right text-[11px] leading-snug text-gray-700">
                <p className="font-semibold uppercase">{biz.legal_name || biz.trade_name}</p>
                <p>Room No. 1006, Sai Nagar Chawl Committee,</p>
                <p>Bhut Bunglow, Devipada Borivali,</p>
                <p>Mumbai Suburban, Maharashtra – 400066</p>
                <p className="mt-1 text-gray-500">{biz.support_email} · {biz.support_phone}</p>
              </div>
            </div>

            <div className="border-t border-gray-300 my-3" />

            {/* Booking ID / Invoice No. row */}
            <div className="grid grid-cols-2 gap-x-8">
              <KV label="Booking ID"     value={<span className="font-semibold">{invoice.booking_id}</span>} />
              <KV label="Invoice No."    value={<span className="font-semibold">{invoice.invoice_number}</span>} />
              <KV label="Booking Ref."   value={invoice.booking_reference || invoice.booking_id} />
              <KV label="Generated"      value={fmtDateTime(invoice.invoice_generated_at || invoice.invoice_date)} />
              <KV label="Place of Supply" value={`${invoice.place_of_supply || addr.state} (${biz.state_code || '27'})`} />
              <KV label="Transactional Type/Category" value={invoice.transaction_category || 'REG/B2C'} />
              <KV label="HSN/SAC"        value={invoice.hsn_sac || '—'} />
              <KV label="Service Description" value={invoice.service_description || '—'} />
              <KV label="PAN"            value={biz.pan} />
              <KV label="GSTIN"          value={biz.gstin} />
              <KV label="Business Type"  value={biz.constitution} />
              <KV label="Tax Payable under RCM" value={invoice.tax_payable_under_rcm || 'No'} />
            </div>

            <div className="border-t border-gray-300 my-3" />

            {/* Customer Name + B2B extras */}
            <KV label="Customer Name" value={<span className="font-semibold">{customer.name}{customer.company_name ? ` · ${customer.company_name}` : ''}</span>} />
            {customer.email && <KV label="Customer Email" value={customer.email} />}
            {customer.phone && <KV label="Customer Mobile" value={customer.phone} />}
            {customer.gstin && <KV label="Customer GSTIN" value={customer.gstin} />}
            {customer.account_type === 'corporate' && (
              <KV label="Billing Address" value={[addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')} />
            )}

            <div className="border-t border-gray-300 my-3" />

            {/* Primary entity block (Hotel / Flight / Bus / Train / Cab / Package) */}
            <div className="grid grid-cols-2 gap-x-8">
              {primaryBlock.map(([k, v]) => <KV key={k} label={k} value={v} />)}
            </div>

            {/* Booking type-specific stay/passenger details */}
            {invoice.booking_type === 'hotel' && (
              <div className="grid grid-cols-4 gap-3 mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                <div><p className="text-[10px] uppercase text-gray-500">Nights</p><p className="text-sm font-bold">{td.nights || '—'}</p></div>
                <div><p className="text-[10px] uppercase text-gray-500">Rooms</p><p className="text-sm font-bold">{td.rooms || '—'}</p></div>
                <div><p className="text-[10px] uppercase text-gray-500">Guests</p><p className="text-sm font-bold">{td.guests || '—'}</p></div>
                <div><p className="text-[10px] uppercase text-gray-500">Room Type</p><p className="text-sm font-bold">{td.room_type || '—'}</p></div>
              </div>
            )}
            {invoice.booking_type === 'flight' && (
              <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                <div><p className="text-[10px] uppercase text-gray-500">Class</p><p className="text-sm font-bold">{td.class || '—'}</p></div>
                <div><p className="text-[10px] uppercase text-gray-500">Passengers</p><p className="text-sm font-bold">{td.passengers || '—'}</p></div>
              </div>
            )}

            {/* PAYMENT BREAKUP header */}
            <div className="text-center mt-5 mb-2">
              <h3 className="text-xs font-extrabold tracking-widest text-gray-900 uppercase">Payment Breakup</h3>
              <div className="h-px bg-gray-300 mt-2" />
            </div>

            {/* Payment rows (MMT pattern) */}
            <div className="px-1">
              <PayRow label={`*${invoice.booking_type === 'hotel' ? 'Accommodation' : 'Booking'} Charges`}
                      sub={pb.booking_charges_note || `Inclusive of applicable taxes collected on behalf of ${supplier.name}`}
                      value={pb.booking_charges ?? invoice.total_amount} bold />
              <PayRow label="Service Fees" value={pb.service_fees ?? 0} />
              <PayRow label="Reversal of Elite Travo Service Fee" value={pb.reversal_service_fees ?? 0} />
              <PayRow label="Effective Discount" value={pb.effective_discount ?? 0} />

              <div className="border-t-2 border-gray-900 mt-2" />
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm font-extrabold">Grand Total</p>
                <p className="text-sm font-extrabold">
                  ₹{Number(pb.grand_total ?? invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <p className="text-[10px] text-gray-500 italic mt-1">Amount in words: {invoice.amount_in_words}</p>
            </div>

            {/* Payment instrument + QR code panel */}
            <div className="grid grid-cols-[1fr_140px] gap-4 mt-4 p-3 border border-gray-200 rounded bg-gray-50 no-break">
              <div className="space-y-1.5">
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Payment Details</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <span className="text-gray-500">Method</span>
                  <span className="font-semibold text-gray-900">{invoice.payment_method || '—'}</span>
                  <span className="text-gray-500">Status</span>
                  <span className="font-semibold uppercase" style={{ color: watermark?.color || '#111' }}>
                    {invoice.payment_status || '—'}
                  </span>
                  <span className="text-gray-500">Transaction ID</span>
                  <span className="font-mono text-[10px] text-gray-900 break-all">{invoice.payment_ref || '—'}</span>
                  <span className="text-gray-500">Booking Ref.</span>
                  <span className="font-mono text-[10px] text-gray-900">{invoice.booking_reference || invoice.booking_id}</span>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center">
                <div className="bg-white p-1.5 border border-gray-300 rounded">
                  <QRCodeSVG value={qrPayload} size={88} level="M" includeMargin={false} />
                </div>
                <p className="text-[9px] text-gray-500 mt-1 text-center leading-tight">Scan to verify<br />{invoice.invoice_number}</p>
              </div>
            </div>

            {/* GST disclaimer */}
            <p className="text-[10px] text-gray-700 mt-4 leading-relaxed">
              Input tax credit of GST charged by the original service provider is available only against the invoice issued by the respective service provider. Elite Travo acts only as a facilitator for these services.
              <br /><span className="font-semibold">This is not a valid travel document.</span>
            </p>

            {/* Terms & Conditions */}
            <div className="mt-5 border-t border-gray-300 pt-3">
              <p className="text-xs font-extrabold uppercase tracking-wider text-gray-900 mb-2">Terms &amp; Conditions</p>
              <ol className="text-[10px] text-gray-700 leading-relaxed list-decimal pl-4 space-y-0.5">
                <li>Any dispute with respect to the invoice is to be reported back to {biz.trade_name} within 48 hours of receipt of invoice.</li>
                <li>QR code for B2B and SEZ category invoices can only be scanned using the GSTN e-invoice app.</li>
                <li>This is a system-generated invoice and does not require signatures.</li>
                <li>Cancellation, refund and reschedule are subject to the supplier&apos;s policy and {biz.trade_name}&apos;s convenience fee. See <span className="text-blue-700">{(biz.website_url || '').replace(/^https?:\/\//, '')}/cancellation-refund-policy</span>.</li>
                <li>For support, write to <span className="text-blue-700">{biz.support_email}</span> or call {biz.support_phone}. Visit <span className="text-blue-700">{(biz.website_url || '').replace(/^https?:\/\//, '')}</span>.</li>
              </ol>
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-start justify-between gap-4 flex-wrap text-[10px] text-gray-600">
              <div>
                <p className="font-semibold text-gray-700">Registered Office · {biz.trade_name} ({biz.constitution})</p>
                <p>{biz.address_oneline}</p>
                <p className="mt-0.5">GSTIN {biz.gstin} · PAN {biz.pan} · State Code {biz.state_code}</p>
              </div>
              <div className="text-right">
                <p>{invoice.invoice_number}</p>
                <p>Page 1 of 1</p>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [active, setActive] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [filters, setFilters] = useState({ booking_type: '', payment_status: '', q: '' });

  // Load production business info (GST cert details + support contacts) once.
  useEffect(() => {
    api.get('/public/business').then((r) => setBusinessInfo(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.booking_type) params.booking_type = filters.booking_type;
      if (filters.payment_status) params.payment_status = filters.payment_status;
      if (filters.q.trim()) params.q = filters.q.trim();
      const [listRes, sumRes] = await Promise.all([
        api.get('/admin/invoices', { params }),
        api.get('/admin/invoices/summary'),
      ]);
      setInvoices(listRes.data?.items || []);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.booking_type, filters.payment_status]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const seed = async () => {
    if (!window.confirm('Generate 30 dummy invoices? (Existing invoices will be cleared.)')) return;
    setSeeding(true);
    try {
      const { data } = await api.post('/admin/invoices/seed-dummy', null, {
        params: { count: 30, wipe: true },
      });
      toast.success(`${data.inserted} dummy invoices generated`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const openInvoice = async (inv) => {
    try {
      const { data } = await api.get(`/admin/invoices/${inv.id}`);
      setActive(data);
    } catch (err) {
      toast.error('Failed to open invoice');
    }
  };

  const emailInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/email`);
      if (data?.mocked) {
        toast.message('Email send mocked', { description: `Would send to ${data.would_send_to} once RESEND_API_KEY is configured.` });
      } else {
        toast.success('Invoice emailed');
      }
    } catch (err) {
      toast.error('Failed to email invoice');
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      toast.success('Invoice deleted');
      setActive(null);
      setInvoices((prev) => prev.filter((x) => x.id !== inv.id));
      // refresh summary in the background
      api.get('/admin/invoices/summary').then((r) => setSummary(r.data)).catch(() => {});
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const totals = useMemo(() => {
    if (summary) return summary;
    return { total: 0, paid: 0, pending: 0, refunded: 0, total_amount: 0, paid_amount: 0, pending_amount: 0 };
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totals.total} invoice{totals.total === 1 ? '' : 's'} on record
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="invoices-refresh-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={seed} disabled={seeding} data-testid="invoices-seed-btn">
            {seeding ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
            Generate Dummy
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiTile testId="kpi-total"      label="Total Invoices"  value={totals.total} />
        <KpiTile testId="kpi-paid"       label="Paid"             value={totals.paid}     accent="emerald" />
        <KpiTile testId="kpi-pending"    label="Pending"          value={totals.pending}  accent="amber" />
        <KpiTile testId="kpi-refunded"   label="Refunded"         value={totals.refunded} accent="blue" />
        <KpiTile testId="kpi-amount"     label="Total Amount"     value={fmtINR(totals.total_amount)} accent="red" />
      </div>

      {/* Filters */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-3 sm:p-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px_auto] gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Search invoice #, customer or booking ID"
                className="h-9 pl-8 text-sm"
                data-testid="invoice-search-input"
              />
            </div>
            <select
              value={filters.booking_type}
              onChange={(e) => setFilters((f) => ({ ...f, booking_type: e.target.value }))}
              className="h-9 px-2 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              data-testid="invoice-filter-type"
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select
              value={filters.payment_status}
              onChange={(e) => setFilters((f) => ({ ...f, payment_status: e.target.value }))}
              className="h-9 px-2 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              data-testid="invoice-filter-status"
            >
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
            </select>
            <Button type="submit" size="sm" data-testid="invoice-search-btn">Apply</Button>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No invoices match your filters.</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">Try “Generate Dummy” to seed 30 realistic samples.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Invoice #</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Customer</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Amount</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {invoices.map((inv) => {
                  const meta = TYPE_META[inv.booking_type] || { label: inv.booking_type, Icon: FileText };
                  const Icon = meta.Icon;
                  return (
                    <tr
                      key={inv.id}
                      onClick={() => openInvoice(inv)}
                      className="hover:bg-red-50/40 dark:hover:bg-red-900/10 cursor-pointer transition-colors"
                      data-testid={`invoice-row-${inv.invoice_number}`}
                    >
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {meta.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        <p className="font-medium text-gray-900 dark:text-white truncate max-w-[260px]">{inv.customer?.name}</p>
                        <p className="text-[11px] text-gray-500 truncate max-w-[260px]">{inv.customer?.email}{inv.customer?.account_type === 'corporate' && inv.customer?.company_name ? ` · ${inv.customer.company_name}` : ''}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">{fmtINR(inv.total_amount)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`uppercase text-[10px] ${STATUS_STYLE[inv.payment_status] || ''}`}>
                          {inv.payment_status === 'paid' && <BadgeCheck className="h-3 w-3 mr-0.5" />}
                          {inv.payment_status === 'pending' && <AlertCircle className="h-3 w-3 mr-0.5" />}
                          {inv.payment_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openInvoice(inv); }} data-testid={`invoice-view-btn-${inv.invoice_number}`}>View</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {active && (
        <InvoicePreview
          invoice={active}
          businessInfo={businessInfo}
          onClose={() => setActive(null)}
          onEmail={emailInvoice}
          onDelete={deleteInvoice}
        />
      )}
    </div>
  );
};

export default AdminInvoices;
